import React, { useState } from 'react';
import { Phone, Mail, Instagram, Heart, Star, Sparkles, PartyPopper, Gem, Calendar, Users, GraduationCap, BookOpen } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const HomePage = () => {
  const [hoveredStyle, setHoveredStyle] = useState(null);

  const braidingStyles = [
    {
      id: 1,
      name: 'Box Braids',
      price: '£15',
      image: '/real_braid_1.jpeg',
      description: 'Classic protective style with square-shaped sections'
    },
    {
      id: 2,
      name: 'Cornrows',
      price: '£12',
      image: '/real_braid_2.jpeg',
      description: 'Traditional braids close to the scalp in straight lines'
    },
    {
      id: 3,
      name: 'Ghana Braids',
      price: '£18',
      image: '/real_braid_3.jpeg',
      description: 'Feed-in braids that start small and get thicker'
    },
    {
      id: 4,
      name: 'Lemonade Braids',
      price: '£16',
      image: '/real_braid_4.jpeg',
      description: 'Side-swept cornrows inspired by Beyoncé'
    },
    {
      id: 5,
      name: 'Knotless Braids',
      price: '£20',
      image: '/real_braid_5.jpeg',
      description: 'Gentle on edges, natural-looking braids'
    },
    {
      id: 6,
      name: 'Twist Braids',
      price: '£14',
      image: '/real_braid_6.jpeg',
      description: 'Two-strand twists for a softer look'
    }
  ];

  const services = [
    { icon: PartyPopper, title: 'Parties', description: 'Make your celebration special with beautiful braids' },
    { icon: Gem, title: 'Weddings', description: 'Elegant bridal braiding for your big day' },
    { icon: Sparkles, title: 'Special Occasions', description: 'Look stunning for any event' },
    { icon: Calendar, title: 'Events', description: 'Professional braiding for all events' },
    { icon: Users, title: 'School Trips', description: 'Neat, long-lasting styles for school' },
    { icon: BookOpen, title: 'Private Classes', description: 'Learn braiding techniques from an expert' }
  ];

  return (
    <div className="homepage">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <img src="/logo_full.png" alt="The Braidy Bunch London" className="header-logo" />
          </div>
          <nav className="nav-links">
            <a href="#about">About</a>
            <a href="#styles">Styles</a>
            <a href="#services">Services</a>
            <a href="#contact">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-braid-accent">
              <img src="/braid_a_small.png" alt="" className="decorative-braid" />
            </div>
            <h1 className="hero-title">
              <span className="title-line">Bringing Hair</span>
              <span className="title-line">To Life</span>
              <Heart className="heart-icon" />
            </h1>
            <p className="hero-subtitle">
              Expert braiding with love and care in North London
            </p>
            <div className="hero-cta">
              <Button className="cta-button" size="lg">
                <Phone className="mr-2" size={20} />
                Book Now
              </Button>
              <a href="https://instagram.com/thebraidybunch_ldn" target="_blank" rel="noopener noreferrer" className="instagram-link">
                <Instagram size={20} />
                @thebraidybunch_ldn
              </a>
            </div>
          </div>
          <div className="hero-decoration">
            <img src="/braid_a.png" alt="" className="hero-braid-decoration" />
            <div className="floating-element element-1"></div>
            <div className="floating-element element-2"></div>
            <div className="floating-element element-3"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="about-container">
          <div className="about-image-wrapper">
            <div className="image-decoration"></div>
            <img 
              src="/alexis_photo.jpeg" 
              alt="Alexis Kolirin" 
              className="about-image"
            />
            <div className="image-badge">
              <Star fill="currentColor" size={16} />
              <span>Expert Braider</span>
            </div>
          </div>
          <div className="about-content">
            <Badge className="section-badge">Meet Alexis</Badge>
            <h2 className="about-title">A Passion That Started at Six</h2>
            <div className="about-text">
              <p>
                Alexis Kolirin is a mother of three beautiful children and an expert braider with a heart of gold. 
                Her journey began at just six years old, when she first discovered the art of braiding.
              </p>
              <p>
                What started as a childhood passion has blossomed into a thriving business, where Alexis brings 
                girls' hair to life with her skilled hands and creative vision. Every braid tells a story, and 
                every client leaves feeling beautiful and confident.
              </p>
              <p>
                Based in North London, Alexis combines traditional braiding techniques with contemporary styles, 
                ensuring that each client receives a unique look that celebrates their individuality.
              </p>
            </div>
            <div className="about-stats">
              <div className="stat-item">
                <div className="stat-number">20+</div>
                <div className="stat-label">Years Experience</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">1000+</div>
                <div className="stat-label">Happy Clients</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">10+</div>
                <div className="stat-label">Braid Styles</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles Section */}
      <section id="styles" className="styles-section">
        <div className="styles-header">
          <Badge className="section-badge">Our Styles</Badge>
          <h2 className="section-title">Beautiful Braids for Everyone</h2>
          <p className="section-subtitle">Prices range from £10 to £20</p>
        </div>
        <div className="styles-grid">
          {braidingStyles.map((style) => (
            <Card 
              key={style.id} 
              className="style-card"
              onMouseEnter={() => setHoveredStyle(style.id)}
              onMouseLeave={() => setHoveredStyle(null)}
            >
              <div className="style-image-wrapper">
                <img src={style.image} alt={style.name} className="style-image" />
                <div className={`style-overlay ${hoveredStyle === style.id ? 'active' : ''}`}>
                  <Sparkles size={24} />
                </div>
              </div>
              <CardContent className="style-content">
                <div className="style-header">
                  <h3 className="style-name">{style.name}</h3>
                  <span className="style-price">{style.price}</span>
                </div>
                <p className="style-description">{style.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="services-header">
          <Badge className="section-badge">What I Do</Badge>
          <h2 className="section-title">Services for Every Occasion</h2>
        </div>
        <div className="services-grid">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card key={index} className="service-card">
                <CardContent className="service-content">
                  <div className="service-icon">
                    <IconComponent size={48} strokeWidth={1.5} />
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <div className="contact-content">
            <Badge className="section-badge">Get In Touch</Badge>
            <h2 className="contact-title">Ready for Beautiful Braids?</h2>
            <p className="contact-text">
              Book your appointment today and let's create something beautiful together!
            </p>
            <div className="contact-methods">
              <a href="tel:07961362590" className="contact-method">
                <div className="method-icon">
                  <Phone size={24} />
                </div>
                <div className="method-details">
                  <div className="method-label">Call or Text</div>
                  <div className="method-value">07961 362 590</div>
                </div>
              </a>
              <a href="https://instagram.com/thebraidybunch_ldn" target="_blank" rel="noopener noreferrer" className="contact-method">
                <div className="method-icon">
                  <Instagram size={24} />
                </div>
                <div className="method-details">
                  <div className="method-label">Follow Us</div>
                  <div className="method-value">@thebraidybunch_ldn</div>
                </div>
              </a>
            </div>
          </div>
          <div className="contact-decoration">
            <div className="decoration-circle circle-1"></div>
            <div className="decoration-circle circle-2"></div>
            <div className="decoration-circle circle-3"></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img src="/logo_full.png" alt="The Braidy Bunch London" className="footer-logo-img" />
            <p>Bringing hair to life with love <Heart size={14} fill="currentColor" /></p>
          </div>
          <div className="footer-links">
            <a href="https://instagram.com/thebraidybunch_ldn" target="_blank" rel="noopener noreferrer">
              <Instagram size={20} />
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 The Braidy Bunch London. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;